import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Sheet, Stack, Typography } from '@mui/joy';

import { useAdvertiser } from '../../../AdvertiserContext';
import { APIAdsCampaignStats, adsErrorText } from '../../../api_ads';
import AdBreakdownTable from './AdBreakdownTable';
import AdLineChart from './AdLineChart';
import AdMetricCard from './AdMetricCard';
import AdPeriodPicker from './AdPeriodPicker';
import { FORMAT_LABELS, formatInt, formatMoney } from '../adsConstants';


const CHART_METRICS = [
    { key: 'impressions', label: 'Показы', format: (v) => formatInt(Math.round(v)) },
    { key: 'clicks', label: 'Клики', format: (v) => formatInt(Math.round(v)) },
    { key: 'ctr', label: 'CTR', format: (v) => `${Number(v).toFixed(2)}%` },
    { key: 'spend', label: 'Расход', format: (v) => `${formatMoney(v)} ₽` },
];

const money = (value) => `${formatMoney(value)} ₽`;
const percent = (value) => `${Number(value || 0).toFixed(2)}%`;


/** Статистика одной кампании: всё нужное одним запросом. */
export default function AdCampaignStats({ campaignId }) {
    const { advertiserToken: token } = useAdvertiser();
    const [period, setPeriod] = useState({ period: '30d', dateFrom: null, dateTo: null });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        if (!token || !campaignId) return;
        try {
            setLoading(true);
            setError(null);
            const res = await APIAdsCampaignStats(token, campaignId, period);
            setData(res);
        } catch (e) {
            setError(adsErrorText(e, 'Не удалось загрузить статистику'));
        } finally {
            setLoading(false);
        }
    }, [token, campaignId, period]);

    useEffect(() => { load(); }, [load]);

    if (loading && !data) {
        return <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>;
    }

    if (error) return <Alert color="danger">{error}</Alert>;

    const current = data?.summary?.current || {};
    const delta = data?.summary?.delta_percent || {};
    const today = data?.today || {};

    return (
        <Box>
            <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', mb: 2 }}>
                <AdPeriodPicker value={period} onChange={setPeriod} />
            </Sheet>

            <Sheet variant="soft" color="primary" sx={{ p: 1.5, borderRadius: 'md', mb: 2 }}>
                <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center">
                    <Chip size="sm" variant="solid" color="primary">Сегодня</Chip>
                    <Typography level="body-sm">Показы <b>{formatInt(today.impressions)}</b></Typography>
                    <Typography level="body-sm">Клики <b>{formatInt(today.clicks)}</b></Typography>
                    <Typography level="body-sm">CTR <b>{percent(today.ctr)}</b></Typography>
                    <Typography level="body-sm">Расход <b>{money(today.spend)}</b></Typography>
                </Stack>
            </Sheet>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <AdMetricCard size="lg" label="Показы"
                              value={formatInt(current.impressions)} delta={delta.impressions} />
                <AdMetricCard size="lg" label="Клики"
                              value={formatInt(current.clicks)} delta={delta.clicks} />
                <AdMetricCard label="CTR" value={percent(current.ctr)} delta={delta.ctr} />
                <AdMetricCard label="Расход" value={money(current.spend)}
                              delta={delta.spend} hint="начислено" />
                <AdMetricCard label="Цена клика" value={money(current.cpc)}
                              delta={delta.cpc} inverted />
            </Stack>

            <Box sx={{ mb: 3 }}>
                <AdLineChart data={data?.timeline || []} metrics={CHART_METRICS} height={220} />
            </Box>

            <Typography level="title-sm" sx={{ mb: 1 }}>По объявлениям</Typography>
            <Box sx={{ mb: 3 }}>
                <AdBreakdownTable
                    defaultSort="impressions"
                    rows={data?.creatives || []}
                    columns={[
                        { key: 'title', label: 'Объявление', width: 240 },
                        {
                            key: 'format', label: 'Формат', width: 160,
                            render: (r) => FORMAT_LABELS[r.format] || r.format,
                        },
                        {
                            key: 'impressions', label: 'Показы', align: 'right', width: 110,
                            render: (r) => formatInt(r.impressions),
                            total: (t) => formatInt(t.impressions),
                        },
                        {
                            key: 'clicks', label: 'Клики', align: 'right', width: 100,
                            render: (r) => formatInt(r.clicks),
                            total: (t) => formatInt(t.clicks),
                        },
                        {
                            key: 'ctr', label: 'CTR', align: 'right', width: 90,
                            render: (r) => percent(r.ctr),
                            total: (t) => percent(t.ctr),
                        },
                        {
                            key: 'spend', label: 'Расход', align: 'right', width: 120,
                            render: (r) => money(r.spend),
                            total: (t) => money(t.spend),
                        },
                    ]}
                />
            </Box>

            <Typography level="title-sm" sx={{ mb: 1 }}>По площадкам</Typography>
            <AdBreakdownTable
                defaultSort="impressions"
                rows={data?.placements || []}
                columns={[
                    { key: 'name', label: 'Площадка', width: 240 },
                    {
                        key: 'impressions', label: 'Показы', align: 'right', width: 110,
                        render: (r) => formatInt(r.impressions),
                        total: (t) => formatInt(t.impressions),
                    },
                    {
                        key: 'clicks', label: 'Клики', align: 'right', width: 100,
                        render: (r) => formatInt(r.clicks),
                        total: (t) => formatInt(t.clicks),
                    },
                    {
                        key: 'ctr', label: 'CTR', align: 'right', width: 90,
                        render: (r) => percent(r.ctr),
                        total: (t) => percent(t.ctr),
                    },
                    {
                        key: 'ecpm', label: 'eCPM', align: 'right', width: 110,
                        render: (r) => money(r.ecpm),
                        total: (t) => money(t.ecpm),
                    },
                    {
                        key: 'spend', label: 'Расход', align: 'right', width: 120,
                        render: (r) => money(r.spend),
                        total: (t) => money(t.spend),
                    },
                ]}
            />
        </Box>
    );
}